import type { MenuOption } from "naive-ui";
import { defineStore } from "pinia";
import { ref, shallowRef, type Component } from "vue";
import type { RouteLocationNormalizedLoaded } from "vue-router";

import AdminSidebarLayout from "../layouts/AdminSidebarLayout.vue";
import PlainLayout from "../layouts/PlainLayout.vue";
import SSOAuthLayout from "../layouts/SSOAuthLayout.vue";

export const LAYOUT_REGISTRY = {
  adminSidebar: AdminSidebarLayout,
  SSOAuth: SSOAuthLayout,
  plain: PlainLayout,
} satisfies Record<string, Component>;

export type LayoutKey = keyof typeof LAYOUT_REGISTRY;

export interface BreadcrumbItem {
  title: string;
  path?: string;
}

export interface AppMetadata {
  appName?: string;
  appDescription?: string;
  appLogo?: string;
}

export interface RoutePageConfig {
  title?: string;
  menuOptions?: MenuOption[];
  activeMenu?: string | null;
  breadcrumbs?: BreadcrumbItem[] | unknown;
  layout?: LayoutKey | "none";
  route?: RouteLocationNormalizedLoaded;
}

/**
 * Minimal user info untuk keperluan tampilan (mis. sidebar footer).
 * Sengaja diminimalkan (bukan full profile) supaya pageStore tidak
 * coupled ke shape authStore/profile module manapun.
 */
export interface PageUserInfo {
  name: string;
  email?: string;
  avatarUrl?: string;
}

export const usePageStore = defineStore("page", () => {
  // --------------------------------------------------------------------------
  // App Metadata (Static / Application-level)
  // --------------------------------------------------------------------------

  const appName = ref<string>("");
  const appDescription = ref<string>("");
  const appLogo = ref<string>("");

  // --------------------------------------------------------------------------
  // Page State (Dynamic / Route-level)
  // --------------------------------------------------------------------------

  const title = ref<string>("");
  // menuOptions arrives already filtered by permission — pageStore has no
  // opinion about permissions, it just renders what it's given.
  const menuOptions = ref<MenuOption[]>([]);
  const activeMenu = ref<string | null>(null);
  const breadcrumbs = ref<BreadcrumbItem[]>([]);
  const route = ref<RouteLocationNormalizedLoaded | null>(null);
  const layout = shallowRef<Component>(PlainLayout);
  const isLoading = ref(false);

  // --------------------------------------------------------------------------
  // UI State
  // --------------------------------------------------------------------------

  const isSidebarCollapsed = ref(false);

  // --------------------------------------------------------------------------
  // User State (Minimal, fed externally — e.g. dari authStore/main.ts)
  // --------------------------------------------------------------------------

  const userInfo = ref<PageUserInfo | null>(null);

  /**
   * Handler logout yang di-inject dari luar (mis. main.ts atau App.vue),
   * supaya pageStore/layout tidak perlu tahu implementasi auth-nya.
   */
  const logoutHandler = shallowRef<(() => void | Promise<void>) | null>(null);

  // --------------------------------------------------------------------------
  // Actions
  // --------------------------------------------------------------------------

  /**
   * Initialize application metadata.
   * Call once from main.ts or App.vue.
   */
  function initAppMetadata(metadata: AppMetadata) {
    if (metadata.appName !== undefined) {
      appName.value = metadata.appName;
    }

    if (metadata.appDescription !== undefined) {
      appDescription.value = metadata.appDescription;
    }

    if (metadata.appLogo !== undefined) {
      appLogo.value = metadata.appLogo;
    }
  }

  /**
   * Update page configuration whenever route changes.
   * Caller (router guard) is responsible for passing menuOptions that are
   * already filtered by the current user's permissions.
   */
  function updatePageConfig(config: RoutePageConfig) {
    title.value = config.title ?? "";
    menuOptions.value = config.menuOptions ?? [];
    activeMenu.value = config.activeMenu ?? null;
    breadcrumbs.value = (config.breadcrumbs as BreadcrumbItem[]) ?? [];
    route.value = config.route ?? null;

    if (config.layout === "none") {
      layout.value = PlainLayout;
    } else if (config.layout && config.layout in LAYOUT_REGISTRY) {
      layout.value = LAYOUT_REGISTRY[config.layout];
    } else {
      layout.value = PlainLayout;
    }
  }

  function toggleSidebar() {
    isSidebarCollapsed.value = !isSidebarCollapsed.value;
  }

  function setSidebarCollapsed(collapsed: boolean) {
    isSidebarCollapsed.value = collapsed;
  }

  function setActiveMenu(key: string | null) {
    activeMenu.value = key;
  }

  function resetPageConfig() {
    title.value = "";
    menuOptions.value = [];
    activeMenu.value = null;
    breadcrumbs.value = [];
    route.value = null;
    layout.value = AdminSidebarLayout;
  }

  /**
   * Feed/update minimal user info dari luar (mis. setelah login berhasil
   * atau saat authStore.profile berubah).
   */
  function setUserInfo(info: PageUserInfo | null) {
    userInfo.value = info;
  }

  function clearUserInfo() {
    userInfo.value = null;
  }

  /**
   * Daftarkan handler logout dari luar, supaya layout tinggal panggil
   * pageStore.triggerLogout() tanpa tahu implementasi auth-nya.
   */
  function setLogoutHandler(handler: (() => void | Promise<void>) | null) {
    logoutHandler.value = handler;
  }

  async function triggerLogout() {
    if (logoutHandler.value) {
      await logoutHandler.value();
    }
  }

  return {
    // ------------------------------------------------------------------------
    // App Metadata
    // ------------------------------------------------------------------------

    appName,
    appDescription,
    appLogo,

    // ------------------------------------------------------------------------
    // Page State
    // ------------------------------------------------------------------------

    title,
    menuOptions,
    activeMenu,
    breadcrumbs,
    route,
    layout,

    // ------------------------------------------------------------------------
    // UI State
    // ------------------------------------------------------------------------

    isSidebarCollapsed,

    // ------------------------------------------------------------------------
    // User State
    // ------------------------------------------------------------------------

    userInfo,

    // ------------------------------------------------------------------------
    // Actions
    // ------------------------------------------------------------------------

    initAppMetadata,
    updatePageConfig,
    toggleSidebar,
    setSidebarCollapsed,
    setActiveMenu,
    resetPageConfig,
    setUserInfo,
    clearUserInfo,
    setLogoutHandler,
    triggerLogout,

    isLoading,
  };
});
